import React, { useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import coreHypothesisService from '../../services/coreHypothesisService';
import { getUserFacingServerError } from '../../utils/apiError';

/**
 * CoreHypothesis — Productivity Tool (REQ-001)
 * Location: frontend/src/components/core-hypothesis/CoreHypothesis.js
 *
 * Stateless evaluator: the user writes a Core Hypothesis, we send it to the
 * AI-only backend (/productivity/core-hypothesis/evaluate) and render the
 * per-part checklist + an example rewrite. Nothing is persisted.
 * Markup/CSS ported 1:1 from docs/prototypes/productivity-tools/index.html.
 */

// Maps the API `part` enum to the human label shown in the checklist.
const PART_LABELS = {
  project: 'Project',
  technology: 'Technology',
  customer_value: 'Customer value',
  business_value: 'Business value',
};

// Pull a user-friendly message out of an axios error without leaking internals.
const friendlyError = (err) => {
  const detail = err?.response?.data?.detail;
  const serverError = getUserFacingServerError(err?.response?.status, detail);
  if (serverError) return serverError;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  if (err?.response?.status === 429) return 'You have reached the usage limit. Please try again shortly.';
  return 'Unable to evaluate your hypothesis right now. Please try again.';
};

const CoreHypothesis = () => {
  const [hypothesis, setHypothesis] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleEvaluate = async () => {
    const text = hypothesis.trim();
    if (!text) {
      setError('Please enter a hypothesis to evaluate.');
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await coreHypothesisService.evaluate(text);
      setResult(data);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setHypothesis('');
    setResult(null);
    setError(null);
  };

  const parts = result?.parts || [];
  const presentCount = parts.filter((p) => p.present).length;
  const allPresent = parts.length > 0 && presentCount === parts.length;

  return (
    <div className="productivity-page">
      <div className="grid-two">
        {/* LEFT: instructions + input */}
        <div className="card">
          <h3>Enter your Core Hypothesis</h3>
          <textarea
            className="input"
            id="chInput"
            aria-label="Write or paste your Core Hypothesis"
            value={hypothesis}
            maxLength={4000}
            onChange={(e) => setHypothesis(e.target.value)}
            placeholder="Write or paste your hypothesis — e.g. The [project] uses [technology] that delivers [customer value] so that we get [business value]."
          />
          <div className="btn-row">
            <button className="btn" onClick={handleEvaluate} disabled={loading}>
              {loading ? 'Evaluating…' : 'Evaluate'}
            </button>
            <button className="btn secondary" onClick={handleClear} disabled={loading}>Clear</button>
          </div>

          <div className="instructions">
            <h3>How to build a Core Hypothesis</h3>
            <p className="lead" style={{ fontSize: '.9rem' }}>One statement, four parts:</p>
            <div className="template-box formula">
              <div><span className="conn">The</span> <span className="slot">product / project code name</span></div>
              <div><span className="conn">uses</span> <span className="slot">technology</span></div>
              <div><span className="conn">that delivers</span> <span className="slot">customer value</span></div>
              <div><span className="conn">so that we get</span> <span className="slot">business value</span><span className="conn">.</span></div>
            </div>

            <ul className="parts-list">
              <li><strong>Project</strong> — The name of the product, feature, or initiative you&apos;re testing. It identifies what the hypothesis is about.</li>
              <li><strong>Technology</strong> — The approach, capability, or solution you&apos;ll use to build it. It describes how you intend to deliver value, and intentionally covers a broad range of solutions.</li>
              <li><strong>Customer value</strong> — The concrete benefit the user gains. It answers what&apos;s in it for the customer.</li>
              <li><strong>Business value</strong> — The results that the business will see from a successful launch of this project. It should be more specific than &ldquo;make money&rdquo; to include things like market share growth, opening new markets, improving strategic position. If you intend to use a specific business model (subscriptions, leasing, IP licensing) include that here.</li>
            </ul>
          </div>
        </div>

        {/* RIGHT: feedback */}
        <div>
          {loading && (
            <div className="card feedback-loading">
              <LoadingSpinner size="large" text="Evaluating your hypothesis…" />
            </div>
          )}

          {!loading && error && (
            <div className="card feedback-error fade-in">
              <strong>Something went wrong</strong>
              {error}
            </div>
          )}

          {!loading && !error && result && (
            <div className="card fade-in">
              <h3>Evaluation</h3>
              <div className={`verdict ${allPresent ? 'good' : 'warn'}`}>
                <span className="ico">{allPresent ? '✓' : '◑'}</span>
                <span>{result.overall}</span>
              </div>

              <ul className="check-list">
                {parts.map((p) => (
                  <li key={p.part}>
                    <span className={`mark ${p.present ? 'yes' : 'no'}`}>{p.present ? '✓' : '!'}</span>
                    <span className="txt">
                      <strong>{PART_LABELS[p.part] || p.part}</strong> — <span>{p.note}</span>
                    </span>
                  </li>
                ))}
              </ul>

              {result.example_rewrite && (
                <div className="rewrite">
                  <span className="label">Example Rewrite</span>
                  {result.example_rewrite}
                </div>
              )}
              <p className="inspiration">Use this as inspiration to improve your core hypothesis.</p>
            </div>
          )}

          {!loading && !error && !result && (
            <div className="card feedback-empty">
              <span>
                Your evaluation will appear here.<br />
                Write your hypothesis, then select <strong>Evaluate</strong>.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoreHypothesis;
